<template>
  <!-- Pagination Info & Controls -->
  <nav
    class="flex justify-center overflow-hidden mt-4 lg:mt-0"
    aria-label="Pagination"
  >
    <div
      class="flex flex-col-reverse lg:flex-row items-center gap-x-6 gap-y-4 lg:gap-y-0"
    >
      <p>
        {{
          displayCommaNumber(
            searchResult?.nbHits === 0
              ? 0
              : ((page || 0) - 1) * itemsPerPage + 1
          )
        }}
        -
        {{
          displayCommaNumber(
            ((page || 0) - 1) * itemsPerPage + (searchResult?.hits.length || 0)
          )
        }}
        {{ $t('products.pagiOf') }}
        {{ displayCommaNumber(searchResult?.nbHits) }}
        {{ $t('products.pagiItem') }}
      </p>

      <!-- Pagination Buttons -->
      <div class="flex items-center gap-x-2">
        <button
          class="cursor-pointer w-8 h-8 flex items-center justify-center rounded-lg"
          @click="$emit('change', page - 1)"
          :disabled="page <= 1"
          aria-label="Previous Page"
        >
          <Icon name="chevron-left" />
        </button>

        <div class="flex flex-row items-center gap-x-2">
          <button
            v-for="item in pageRange"
            :key="item"
            :class="
              twJoin(
                'w-8 h-8 cursor-pointer flex items-center justify-center rounded-lg select-none',
                page === item && 'bg-black text-primary-white'
              )
            "
            @click="$emit('change', item)"
            :aria-current="page === item ? 'page' : undefined"
          >
            {{ item }}
          </button>
        </div>

        <button
          class="cursor-pointer flex items-center justify-center rounded-lg"
          @click="$emit('change', page + 1)"
          :disabled="page >= (searchResult?.nbPages || 1)"
          aria-label="Next Page"
        >
          <Icon name="chevron-right" />
        </button>
      </div>

      <!-- Items per page -->
      <div class="items-center gap-x-2 flex">
        <label for="itemsPerPageSelect"
          >{{ $t('products.pagiPerPage') }}:</label
        >
        <DropdownSelect
          id="itemsPerPageSelect"
          intent="button"
          label=""
          :options="itemsPerPageOption"
          :default="itemsPerPage"
          @change="
            (nextItemsPerPage) => {
              $emit('change', 1)
              $emit('changeItemsPerPage', nextItemsPerPage)
            }
          "
        />
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { twJoin } from 'tailwind-merge'

// Emits and props
const emit = defineEmits<{
  (e: 'change', page?: number): void
  (e: 'search'): void
  (e: 'changeItemsPerPage', itemsPerPage?: number): void
}>()

const props = defineProps<{
  itemsPerPageOption: any
  itemsPerPage: number
  page: number
  searchResult: any
}>()

const totalPage = computed(() => props.searchResult?.nbPages || 0)
const currentPage = computed(() => props.page || 1)
const pageRange = computed(() => {
  const start = Math.max(1, currentPage.value - 2)
  const end = Math.min(totalPage.value, start + 4)

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
})
</script>
